import {
  EnvironmentProviders,
  inject,
  Injectable,
  Injector,
  isDevMode,
  makeEnvironmentProviders,
  Provider,
  runInInjectionContext,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LocationStrategy } from '@angular/common';

import { catchError, EMPTY, Observable } from 'rxjs';

import { httpApiClientFactory } from '@ciphr/utils/data-adapters';

import { NAVIGATION_DATA_ADAPTER } from './navigation-data-adapter.token';
import { NAVIGATION_PROXY_URL } from './navigation-proxy-url.token';
import { NavigationDataAdapter } from './navigation-data-adapter.interface';
import { NavigationPositionItem } from '../models/navigation-position-item.type';
import { NavigationProductItem } from '../models/navigation-product-item.type';

@Injectable({ providedIn: 'root' })
export class NavigationApiDataAdapterProvider implements NavigationDataAdapter {
  private readonly locationStrategy = inject(LocationStrategy);
  private readonly httpClient = inject(HttpClient);
  private readonly injector = inject(Injector);
  private readonly navigationProxyUrl = inject(NAVIGATION_PROXY_URL, { optional: true });

  /**
   * Fetch navigation positions for each product (from the list fetched through the fetchProducts method) that has itemsUrl property.
   *
   * The proxyUrl that is optionally provided along with this data adapter is meant for the SSO app that lacks the access token used
   * for navigation items requests authentication when calling different APIs (like. /hr/api/v1). Proxy endpoint is responsible for making
   * a request for items on behalf of SSO app transforming session cookie used by the SSO app into an access token expected by products'
   * APIs. There is an exception if it's a call from SSO to SSO then it isn't passed through the proxy endpoint.
   *
   * @param {string} itemsUrl - url from itemsUrl property of NavigationProductItem
   */
  fetchPositions(itemsUrl: string): Observable<NavigationPositionItem[]> {
    const proxyUrl = this.navigationProxyUrl && !itemsUrl.startsWith('/sso/api') ? `${this.navigationProxyUrl}?url=` : '';

    return this.httpClient.get<NavigationPositionItem[]>(proxyUrl + itemsUrl).pipe(catchError(() => EMPTY));
  }

  /**
   * Fetch products that will be visible in the navigation panel along with information where navigation items for each product should be
   * fetched from.
   *
   * The if statement is meant to handle requests from client apps (like HR or MyPay) on a localhost environment. Those requests are not
   * authenticated correctly when executed through Angular's proxy, therefore, have to be done directly to the dev environment.
   *
   * @param {boolean} mobile - indicates if information about products should be fetched for a mobile version
   */
  fetchProducts(mobile = false): Observable<NavigationProductItem[]> {
    const endpoint = `/navigation/products${mobile ? '/mobile' : ''}`;

    if (isDevMode() && this.locationStrategy.getBaseHref().includes('app')) {
      return this.httpClient.get<NavigationProductItem[]>('https://dev.ciphr.com/sso/api/v1' + endpoint, { withCredentials: true });
    } else {
      const httpClient = runInInjectionContext(this.injector, httpApiClientFactory({ name: 'sso', version: 1 }));
      return httpClient.get<NavigationProductItem[]>(endpoint);
    }
  }
}

export const provideNavigationApiDataAdapter = (navigationProxyUrl?: string): EnvironmentProviders => {
  const providers: Provider[] = [{ provide: NAVIGATION_DATA_ADAPTER, useClass: NavigationApiDataAdapterProvider }];

  navigationProxyUrl && providers.push({ provide: NAVIGATION_PROXY_URL, useValue: navigationProxyUrl });

  return makeEnvironmentProviders(providers);
};
